import { AfterViewInit, Component, ElementRef, Inject, Injectable, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonService } from '@core/services/common.service';
import { ModalService } from '@core/services/modal.service';
import { TravelMemberService } from '@core/services/travel-member.service';
import { InsuredTrip, InsuredTrips } from '@shared/models/policies/detail/insured-trip/insured-trip';
import moment from 'moment';
import { InsuredTripsBaseComponent } from './insured-trips-base.component';
import { NxDropdownComponent } from '@aposin/ng-aquila/dropdown';
import { InsuredTripListRequest } from '@shared/models/policies/detail/insured-trip/insured-trip-list-request';
import { AlertService } from '@core/services/alert.service';
import { TranslateService } from '@ngx-translate/core';
import { WorkerDone } from '@shared/models/worker/worker-done';
import { NX_DATE_LOCALE, NxDateAdapter, NxDatepickerIntl } from '@aposin/ng-aquila/datefield';
import { LocaleService } from '@core/services/locale.service';

@Injectable()
export class MyIntl extends NxDatepickerIntl {
    constructor(private translateService: TranslateService) {
        super();
        this.switchToMonthViewLabel = this.translateService.instant('common.date.month-view');
        this.switchToMultiYearViewLabel = this.translateService.instant('common.date.date-view');
    }
}
@Component({
    selector: 'app-insured-trips',
    templateUrl: './insured-trips.component.html',
    styleUrls: ['./insured-trips.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class InsuredTripsComponent extends InsuredTripsBaseComponent implements OnInit {
    @ViewChild('personSurname') personSurnameInput: ElementRef<HTMLInputElement>;

    // Dropdown with statusIds
    public dropdownValues = {};


    public activePage = 0;
    public minPage: number = 0;
    public maxPage: number = 0;
    public itemsPerPage = 20;
    public variant = 'short';

    private tableData: Array<InsuredTrip> = [];
    private allData: Array<InsuredTrip> = [];
    private policyNumber: string;

    public pageTableData: Array<InsuredTrip> = [];

    public form: FormGroup;

    public travelStatusIdDefault: number;

    constructor(
        public commonService: CommonService,
        protected router: Router,
        protected route: ActivatedRoute,
        protected modalService: ModalService,

        private travelMemberService: TravelMemberService,
        private alertService: AlertService,
        private translateService: TranslateService,
        private localeService: LocaleService,
        @Inject(NX_DATE_LOCALE) private nxDateLocale: string,
        @Inject(NxDatepickerIntl) private intl: NxDatepickerIntl,
    ) {
        super(commonService);

        this.translateService.get('common.date.month-view').subscribe( x => {
            this.intl.switchToMonthViewLabel = x;
        })

        this.translateService.get('common.date.date-view').subscribe( x => {
            this.intl.switchToMultiYearViewLabel = x;
        })

        translateService.onLangChange.subscribe(x => {
            let lang = this.localeService.languages.find(y => y.code.toLowerCase() == x.lang);
            if (lang && lang.iso) {
                intl.switchToMonthViewLabel = translateService.instant('common.date.month-view');
                intl.switchToMultiYearViewLabel = translateService.instant('common.date.date-view');
                this.nxDateLocale = lang.iso;
            }
        });
    }

    ngOnInit(): void {
        this.form = new FormGroup({
            policyNumber: new FormControl(),
            dateBegin: new FormControl(),
            statusId: new FormControl(),
            fromToChecked: new FormControl(),
            dateFrom: new FormControl(),
            dateTo: new FormControl(),
            dateBirth: new FormControl(),
            personSurname: new FormControl(),
            sortByPolicyNumber: new FormControl(),
        });

        this.form.valueChanges.subscribe( values => {
            this.form.setErrors(null);

            if(moment.isMoment(values.dateFrom) && moment.isMoment(values.dateTo)){
                if(moment(values.dateFrom).isAfter(moment(values.dateTo))){
                    this.form.setErrors({
                        dateFrom: 'greater'
                    })
                }
            }
        })

        // subscribe router param policyNumber
        this.route.params.subscribe(params => {
            if (params.policyNumber != undefined && params.policyNumber.length > 0) {
                this.policyNumber = params.policyNumber;

                this.travelMemberService.getWorkerData(this.policyNumber).subscribe((workerData) => {
                    if (workerData.active.length) {
                        this.translateService.get('policy.insured-trips.active-worker').subscribe(translation => {
                            this.alertService.info(translation, { autoClose: true }); // Show info worker data
                        });
                    } else {
                        const workerDone = new WorkerDone();
                        if(workerData.error && workerData.error.length){
                            this.translateService.get('policy.insured-trips.worker-error').subscribe(translation => {
                                this.alertService.error(translation, { autoClose: true }); // Show error worker data
                            });
                            workerData.error.forEach( wData => {
                                workerDone.ids.push(wData.id);
                            })
                        }
                        
                        if(workerData.finished){
                            workerData.finished.forEach( wData => {
                                workerDone.ids.push(wData.id);
                            })
                        }

                        this.travelMemberService.updateWorkerDone(workerDone).subscribe();
                    }
                });

                // if policyNumber exists on input, then load data for dropdown
                this.travelMemberService.getTravelStatusIds().subscribe(travelStatusIds => {
                    if (travelStatusIds) {
                        this.dropdownValues = travelStatusIds.valuesLabels;
                        this.travelStatusIdDefault = travelStatusIds.defaultValue;

                        this.form.patchValue({
                            statusId: this.travelStatusIdDefault.toString()
                        });

                        const filterValues = JSON.parse(localStorage.getItem('filterValues'));
                        if(filterValues){
                            this.form.patchValue(filterValues);
                        }

                        this.search();
                    }
                });
            }
        });
    }

    getTableData(page?: number): void {
        if (page == null) {
            page = 0;
        }

        this.pageTableData = this.tableData.slice(page * this.itemsPerPage, (page + 1) * this.itemsPerPage);
        this.computeMaxPage();
    }

    handleChangePage($event: string) {
        if ($event == 'Next') {
            this.activePage = (this.activePage < this.maxPage) ? ++this.activePage : this.maxPage;
        } else {
            this.activePage = (this.activePage > this.minPage) ? --this.activePage : this.minPage;
        }

        this.getTableData(this.activePage);
    }

    reportModalShow(selector: string) {
        this.modalService.openModal(selector, this.policyNumber);
    }

    newInsuredTrip() {
        this.router.navigate(['nova-cesta'], {
            relativeTo: this.route
        });
    }

    editInsuredTrip(policyNumber: string) {
        this.router.navigate([policyNumber], {
            relativeTo: this.route
        });
    }

    search() {
        if(!this.form.valid){
            return;
        }

        const searchData = new InsuredTripListRequest();
        searchData.dateBegin = this.form.value.fromToChecked ? null : this.form.value.dateBegin;
        searchData.dateFrom = !this.form.value.fromToChecked ? null : this.form.value.dateFrom;
        searchData.dateTo = !this.form.value.fromToChecked ? null : this.form.value.dateTo;

        searchData.statusId = this.form.value.statusId;
        searchData.personSurname = this.form.value.personSurname;
        searchData.fromToChecked = this.form.value.fromToChecked;
        searchData.policyNumberTravelMember = this.form.value.policyNumber;
        searchData.dateBirth = this.form.value.dateBirth;
        searchData.sortByPolicyNumber = this.form.value.sortByPolicyNumber;

        this.travelMemberService.postTravelMemberList(this.policyNumber, searchData).subscribe((insuredTrips: InsuredTrips) => {
            localStorage.setItem('filterValues', JSON.stringify(this.form.value));
            insuredTrips.list.map((insuredTrip: InsuredTrip) => {
                // entry param set to tarrif
                insuredTrip.scheme = this.moduleNameByTarrif(insuredTrip.tariff);
            });

            this.allData = this.tableData = insuredTrips.list;
            this.getTableData();
        });
    }

    resetAll() {
        localStorage.removeItem('filterValues');
        this.form.reset();
        this.form.patchValue({
            statusId: this.travelStatusIdDefault.toString()
        });
        this.search();
    }

    private computeMaxPage() {
        this.maxPage = Math.ceil(this.tableData.length / this.itemsPerPage);
        if (this.maxPage > 0) {
            this.maxPage = this.maxPage - 1;
        }

        if (this.maxPage < this.activePage) {
            this.activePage = this.maxPage;
        }
    }
}
